// Logistikk / forsyning: LOGSTAT (forsyningsstatus per enhet, forsyningsklasse-
// nivå grønn/gul/rød/svart) og forsynings-anmodning (resupply / CASEVAC-lignende
// flyt med DZ/LZ-punkt). Deles i sanntid over hubben. Gir sjefen forsyningsbilde.
//
// Rent + testbart; DoS-caps på alle fritekstfelt.
import { z } from 'zod';
// ── Forsyningsklasser + nivå ────────────────────────────────────────────────
export const SupplyClass = z.enum(['ammo', 'fuel', 'water', 'rations', 'medical', 'batteries']);
export const SUPPLY_CLASS_LABEL = {
    ammo: 'Ammunisjon',
    fuel: 'Drivstoff',
    water: 'Vann',
    rations: 'Forpleining',
    medical: 'Sanitet',
    batteries: 'Batterier',
};
export const SupplyLevel = z.enum(['green', 'amber', 'red', 'black']);
export const SUPPLY_LEVEL_LABEL = {
    green: 'Grønn (>75%)', amber: 'Gul (30–75%)', red: 'Rød (<30%)', black: 'Svart (tom)',
};
export const SUPPLY_LEVEL_COLOR = {
    green: '#4bd15b', amber: '#ffd24a', red: '#ff3b3b', black: '#1a1a1a',
};
const LEVEL_RANK = { black: 0, red: 1, amber: 2, green: 3 };
/**
 * Forsyningsnivå → militær presedens. ÉN oversettelse (§3), brukt av
 * hendelsesloggens hastegrads-stripe (UX-funn F8).
 *
 * ⚠ SVART ER IMMEDIATE, RØD ER PRIORITY — og forskjellen er hele poenget. Svart
 * betyr TOM: avdelingen har ikke lenger noe å bruke, og ingen ny beslutning kan
 * endre det uten forsyning. Rød (<30 %) betyr at det haster å planlegge, ikke at
 * det haster dette minuttet. Ga vi begge IMMEDIATE, ville haste-båndet vært fullt
 * av røde LOGSTAT-er hver kveld, og det ville skjult den ene svarte.
 */
export const SUPPLY_LEVEL_PRECEDENCE = {
    black: 'immediate',
    red: 'priority',
    amber: 'routine',
    green: 'routine',
};
// ── LOGSTAT (forsyningsstatus per enhet) ────────────────────────────────────
export const LogStatSchema = z.object({
    id: z.string().max(64),
    unit: z.string().max(64),
    /** Nivå per forsyningsklasse (utelatte klasser = ukjent). */
    levels: z.record(SupplyClass, SupplyLevel).default({}),
    note: z.string().max(500).default(''),
    reportedBy: z.string().max(128).default(''),
    tsMs: z.number(),
    updatedMs: z.number().default(0),
});
/** Verste (mest kritiske) nivå på tvers av klassene — til rollup/alarm. */
export function worstLevel(ls) {
    const vals = Object.values(ls.levels);
    if (!vals.length)
        return null;
    return vals.reduce((worst, v) => (LEVEL_RANK[v] < LEVEL_RANK[worst] ? v : worst), vals[0]);
}
/** Monotonisk fletting (sanntid): nyeste updatedMs vinner. */
export function mergeLogStat(a, b) {
    return b.updatedMs >= a.updatedMs ? b : a;
}
// ── Forsynings-anmodning (resupply) ─────────────────────────────────────────
export const ResupplyPriority = z.enum(['routine', 'priority', 'immediate']);
const RESUPPLY_RANK = { immediate: 0, priority: 1, routine: 2 };
export const ResupplyStatus = z.enum(['open', 'approved', 'enroute', 'delivered', 'cancelled']);
export const RESUPPLY_STATUS_LABEL = {
    open: 'Åpen', approved: 'Godkjent', enroute: 'Underveis', delivered: 'Levert', cancelled: 'Avlyst',
};
export const ResupplyRequestSchema = z.object({
    id: z.string().max(64),
    requester: z.string().max(128),
    supplyClass: SupplyClass,
    /** Mengde/beskrivelse (fritekst, f.eks. «2 kasser 5.56, 1 jerrykanne»). */
    quantity: z.string().max(500),
    priority: ResupplyPriority.default('routine'),
    status: ResupplyStatus.default('open'),
    /** DZ/LZ-punkt for levering. */
    lat: z.number().min(-90).max(90).optional(),
    lon: z.number().min(-180).max(180).optional(),
    note: z.string().max(500).default(''),
    tsMs: z.number(),
    updatedMs: z.number().default(0),
});
/** Parsere for trygt collab-inntak (null om ugyldig). */
export function parseLogStatOne(raw) {
    const r = LogStatSchema.safeParse(raw);
    return r.success ? r.data : null;
}
export function parseResupplyOne(raw) {
    const r = ResupplyRequestSchema.safeParse(raw);
    return r.success ? r.data : null;
}
/** Monotonisk fletting: framdrift kan aldri gå bakover + nyeste vinner. */
// delivered og cancelled må ha ULIKE rangeringer, ellers blir mergeResupply
// ikke-kommutativ og to klienter kan konvergere til hver sin terminaltilstand
// (én ser «Levert», en annen «Avlyst»). Levert er en fysisk kjensgjerning →
// dominerer en sen avlysning.
const RESUPPLY_STATUS_RANK = {
    open: 0, approved: 1, enroute: 2, cancelled: 3, delivered: 4,
};
export function mergeResupply(a, b) {
    const winner = b.updatedMs >= a.updatedMs ? b : a;
    const status = RESUPPLY_STATUS_RANK[a.status] >= RESUPPLY_STATUS_RANK[b.status] ? a.status : b.status;
    return { ...winner, status };
}
/** Sorter anmodninger: hastegrad først (immediate→routine), åpne over lukkede. */
export function sortResupply(rs) {
    const openish = (s) => (s === 'delivered' || s === 'cancelled' ? 1 : 0);
    return [...rs].sort((a, b) => openish(a.status) - openish(b.status)
        || RESUPPLY_RANK[a.priority] - RESUPPLY_RANK[b.priority]
        || b.tsMs - a.tsMs);
}
/** Neste naturlige resupply-status (open→approved→enroute→delivered). */
export function nextResupplyStatus(s) {
    const order = ['open', 'approved', 'enroute', 'delivered'];
    const i = order.indexOf(s);
    return i >= 0 && i < order.length - 1 ? order[i + 1] : null;
}
//# sourceMappingURL=logistics.js.map